import { Helmet } from "react-helmet-async";

const SITE_URL = "https://izicvs.com";
const DEFAULT_IMAGE = `${SITE_URL}/screenshots/builder.jpg`;

type Props = {
  // Page title without the brand suffix (added automatically unless already present).
  title: string;
  description?: string;
  // Path for the canonical URL and og:url, e.g. "/pricing".
  path?: string;
  image?: string;
  lang?: string;
  noindex?: boolean;
  // When set, a BreadcrumbList schema (Izicvs > name) is added for this page.
  breadcrumb?: string;
  // One or more schema.org JSON-LD objects rendered as <script> tags.
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
};

// Per-page SEO: title, description, canonical, Open Graph, Twitter card and
// optional JSON-LD. Site-wide defaults live in index.html; this overrides them
// per route so every page has a unique, indexable title and canonical URL.
export const Seo = ({
  title,
  description,
  path = "/",
  image = DEFAULT_IMAGE,
  lang,
  noindex,
  breadcrumb,
  jsonLd,
}: Props) => {
  const url = `${SITE_URL}${path}`;
  const fullTitle = title.includes("Izicvs") ? title : `${title} | Izicvs`;
  const schemas = jsonLd ? (Array.isArray(jsonLd) ? [...jsonLd] : [jsonLd]) : [];

  if (breadcrumb && path !== "/") {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Izicvs", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: breadcrumb, item: url },
      ],
    });
  }

  return (
    <Helmet prioritizeSeoTags>
      {lang && <html lang={lang} />}

      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Izicvs" />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />

      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};
